import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A trace is a group that contains multiple spans, generations, and embeddings. Traces can be manually sent as events or appear as pseudo-events automatically created from child events.`}</p>
    <p><strong parentName="p">{`Event name`}</strong>{`: `}<inlineCode parentName="p">{`$ai_trace`}</inlineCode>{`  `}</p>
    <h3 {...{
      "id": "core-properties"
    }}>{`Core properties`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_trace_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The trace ID (a UUID to group related AI events together)`}<br />{`Must contain only letters, numbers, and special characters: `}<inlineCode parentName="td">{`-`}</inlineCode>{`, `}<inlineCode parentName="td">{`_`}</inlineCode>{`, `}<inlineCode parentName="td">{`~`}</inlineCode>{`, `}<inlineCode parentName="td">{`.`}</inlineCode>{`, `}<inlineCode parentName="td">{`@`}</inlineCode>{`, `}<inlineCode parentName="td">{`(`}</inlineCode>{`, `}<inlineCode parentName="td">{`)`}</inlineCode>{`, `}<inlineCode parentName="td">{`!`}</inlineCode>{`, `}<inlineCode parentName="td">{`'`}</inlineCode>{`, `}<inlineCode parentName="td">{`:`}</inlineCode>{`, `}<code>{`|`}</code><br />{`Example: `}<inlineCode parentName="td">{`d9222e05-8708-41b8-98ea-d4a21849e761`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_session_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Groups related traces together. Use this to organize traces by whatever grouping makes sense for your application (user sessions, workflows, conversations, or other logical boundaries).`}<br />{`Example: `}<inlineCode parentName="td">{`session-abc-123`}</inlineCode>{`, `}<inlineCode parentName="td">{`conv-user-456`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_input_state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The input of the whole trace`}<br />{`Example: `}<inlineCode parentName="td">{`[{"role": "user", "content": "What's the weather in SF?"}]`}</inlineCode>{` or any JSON-serializable state`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_output_state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The output of the whole trace`}<br />{`Example: `}<inlineCode parentName="td">{`[{"role": "assistant", "content": "The weather in San Francisco is..."}]`}</inlineCode>{` or any JSON-serializable state`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_latency`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The latency of the trace in seconds`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_span_name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The name of the trace`}<br />{`Example: `}<inlineCode parentName="td">{`chat_completion`}</inlineCode>{`, `}<inlineCode parentName="td">{`rag_pipeline`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_is_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Boolean to indicate if the trace encountered an error`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The error message or object if the trace failed`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "pseudo-trace-events"
    }}>{`Pseudo-trace Events`}</h3>
    <p>{`When you send generation (`}<inlineCode parentName="p">{`$ai_generation`}</inlineCode>{`), span (`}<inlineCode parentName="p">{`$ai_span`}</inlineCode>{`), or embedding (`}<inlineCode parentName="p">{`$ai_embedding`}</inlineCode>{`) events with a `}<inlineCode parentName="p">{`$ai_trace_id`}</inlineCode>{`, PostHog automatically creates a pseudo-trace event that appears in the dashboard as a parent grouping. These pseudo-traces:`}</p>
    <ul>
      <li parentName="ul">{`Are not actual events in your data`}</li>
      <li parentName="ul">{`Automatically aggregate metrics from child events (latency, tokens, costs)`}</li>
      <li parentName="ul">{`Provide a hierarchical view of your AI operations`}</li>
      <li parentName="ul">{`Do not require sending an explicit `}<inlineCode parentName="li">{`$ai_trace`}</inlineCode>{` event`}</li>
    </ul>
    <p>{`This means you can either:`}</p>
    <ol>
      <li parentName="ol">{`Send explicit `}<inlineCode parentName="li">{`$ai_trace`}</inlineCode>{` events to control the trace metadata`}</li>
      <li parentName="ol">{`Let PostHog automatically create pseudo-traces from your generation/span events`}</li>
    </ol>
    <h3 {...{
      "id": "example-api-call"
    }}>{`Example API call`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -X POST "<ph_client_api_host>/i/v0/e/" \\
     -H "Content-Type: application/json" \\
     -d '{
         "api_key": "<ph_project_api_key>",
         "event": "$ai_trace",
         "properties": {
             "distinct_id": "user_123",
             "$ai_trace_id": "d9222e05-8708-41b8-98ea-d4a21849e761",
             "$ai_session_id": "session-abc-123",
             "$ai_input_state": [{"role": "user", "content": "Tell me a fun fact about hedgehogs"}],
             "$ai_output_state": [{"role": "assistant", "content": "Hedgehogs are small mammals with spines on their back."}],
             "$ai_latency": 1.23,
             "$ai_span_name": "hedgehog_facts_chat",
             "$ai_is_error": false
         },
         "timestamp": "2025-01-30T12:00:00Z"
     }'
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      